import { Button, Input } from "@aix/ui"
import { ReactTable } from "@aix/ui"
import { Edit2, Eye, Plus, Search, Trash2, Video } from "lucide-react"
import { useMemo, useState } from "react"
import toast from "react-hot-toast"
import { useNavigate } from "@tanstack/react-router"
import { trpc } from "../../lib/trpc"

export default function AdminVideosScreen() {
  const navigate = useNavigate()
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [editingVideo, setEditingVideo] = useState<any>(null)
  const [searchQuery, setSearchQuery] = useState("")

  const { data: videos, isLoading, refetch } = trpc.videoOnboarding.listVideos.useQuery({ limit: 100 })
  const uploadMutation = trpc.videoOnboarding.uploadVideo.useMutation({
    onSuccess: () => {
      toast.success("Video uploaded successfully!")
      setIsUploadModalOpen(false)
      void refetch()
    },
    onError: (error) => {
      toast.error(error.message || "Failed to upload video")
    },
  })

  const updateMutation = trpc.videoOnboarding.updateVideo.useMutation({
    onSuccess: () => {
      toast.success("Video updated successfully!")
      setEditingVideo(null)
      void refetch()
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update video")
    },
  })

  const deleteMutation = trpc.videoOnboarding.deleteVideo.useMutation({
    onSuccess: () => {
      toast.success("Video deleted successfully!")
      void refetch()
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete video")
    },
  })

  const filteredVideos = useMemo(() => {
    if (!videos) return []
    if (!searchQuery) return videos
    const query = searchQuery.toLowerCase()
    return videos.filter(
      (v) =>
        v.title.toLowerCase().includes(query) ||
        v.description?.toLowerCase().includes(query) ||
        v.tags?.some((t) => t.toLowerCase().includes(query)),
    )
  }, [videos, searchQuery])

  const columns = useMemo(
    () => [
      {
        Header: "Title",
        accessor: "title",
        Cell: ({ row }: any) => (
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-primary">{row.original?.title || row.title}</span>
              <span className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs uppercase font-medium">
                {row.original?.videoType || row.videoType || "loom"}
              </span>
            </div>
            {(row.original?.description || row.description) && (
              <div className="text-sm text-muted-foreground line-clamp-1">
                {row.original?.description || row.description}
              </div>
            )}
          </div>
        ),
      },
      {
        Header: "Duration",
        accessor: "duration",
        Cell: ({ row }: any) => {
          const duration = row.original?.duration || row.duration
          return duration ? `${Math.ceil(duration / 60)} min` : "-"
        },
      },
      {
        Header: "Tags",
        accessor: "tags",
        Cell: ({ row }: any) => {
          const tags = row.original?.tags || row.tags
          return (
            <div className="flex flex-wrap gap-1">
              {tags?.map((tag: string) => (
                <span key={tag} className="px-2 py-1 bg-secondary rounded text-xs">
                  {tag}
                </span>
              ))}
            </div>
          )
        },
      },
      {
        Header: "Actions",
        accessor: "id",
        Cell: ({ row }: any) => {
          // Use row.original for React Table v7 compatibility
          const video = row.original || row
          return (
            <div className="flex gap-2">
              <button
                onClick={() =>
                  navigate({
                    to: "/onboarding/video/$videoId",
                    params: { videoId: video.id },
                  })
                }
                className="p-2 hover:bg-accent rounded transition-colors group relative"
                title="View video"
              >
                <Eye className="w-4 h-4 text-muted-foreground" />
                <div className="absolute left-0 top-full mt-2 w-24 bg-gray-900 text-white text-xs rounded-lg p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-xl">
                  View video
                </div>
              </button>
              <button
                onClick={() => setEditingVideo(video)}
                className="p-2 hover:bg-accent rounded transition-colors group relative"
                title="Edit video"
              >
                <Edit2 className="w-4 h-4 text-muted-foreground" />
                <div className="absolute left-0 top-full mt-2 w-20 bg-gray-900 text-white text-xs rounded-lg p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-xl">
                  Edit
                </div>
              </button>
              <button
                onClick={() => {
                  if (confirm(`Delete "${video.title}"?`)) {
                    deleteMutation.mutate({ videoId: video.id })
                  }
                }}
                className="p-2 hover:bg-destructive/10 rounded transition-colors group relative"
                title="Delete video"
              >
                <Trash2 className="w-4 h-4 text-destructive" />
                <div className="absolute left-0 top-full mt-2 w-20 bg-gray-900 text-white text-xs rounded-lg p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-xl">
                  Delete
                </div>
              </button>
            </div>
          )
        },
      },
    ],
    [deleteMutation, navigate],
  )

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-primary mb-2">Manage Videos</h1>
            <p className="text-muted-foreground">Upload and manage training videos</p>
          </div>
          <Button onClick={() => setIsUploadModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Upload Video
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search videos..."
            className="pl-10"
          />
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : filteredVideos.length > 0 ? (
          <ReactTable data={filteredVideos} columns={columns} />
        ) : (
          <div className="text-center py-12 bg-secondary rounded-lg">
            <Video className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No videos found</p>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <UploadVideoModal
          onClose={() => setIsUploadModalOpen(false)}
          onSubmit={(data) => uploadMutation.mutate(data)}
          isLoading={uploadMutation.isLoading}
        />
      )}

      {/* Edit Modal */}
      {editingVideo && (
        <EditVideoModal
          video={editingVideo}
          onClose={() => setEditingVideo(null)}
          onSubmit={(data) => updateMutation.mutate({ videoId: editingVideo.id, ...data })}
          isLoading={updateMutation.isLoading}
        />
      )}
    </div>
  )
}

// Upload Video Modal Component
function UploadVideoModal({
  onClose,
  onSubmit,
  isLoading,
}: {
  onClose: () => void
  onSubmit: (data: any) => void
  isLoading: boolean
}) {
  const [videoUrl, setVideoUrl] = useState("")
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [tags, setTags] = useState("")

  // Fetch supported video types
  const { data: videoTypesData } = trpc.videoOnboarding.getSupportedVideoTypes.useQuery()
  const supportedTypes = videoTypesData?.types.filter((t) => t.supported) || []

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      url: videoUrl,
      title: title || undefined,
      description: description || undefined,
      tags: tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-background rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-2xl font-bold text-primary mb-4">Upload Video</h2>

        {/* Supported Sources Info */}
        <div className="mb-4 p-3 bg-secondary rounded-lg">
          <p className="text-sm text-muted-foreground mb-2">Supported video sources:</p>
          <div className="flex flex-wrap gap-2">
            {supportedTypes.map((type) => (
              <span key={type.type} className="px-2 py-1 bg-background rounded text-xs">
                {type.name}
                {type.autoTranscript && " ✓"}
              </span>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">✓ = Auto-transcript available</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Video URL *</label>
            <Input
              type="url"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://www.loom.com/share/... or https://example.com/video.mp4"
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              Paste a Loom URL, YouTube URL, Vimeo URL, or direct MP4 link
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              Title (optional - auto-detected when available)
            </label>
            <Input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Video title" />
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this video covers..."
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              rows={3}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Tags (comma-separated)</label>
            <Input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="claude-code, deployment, beginner"
            />
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" loading={isLoading} className="flex-1">
              Upload
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Edit Video Modal Component
function EditVideoModal({
  video,
  onClose,
  onSubmit,
  isLoading,
}: {
  video: any
  onClose: () => void
  onSubmit: (data: any) => void
  isLoading: boolean
}) {
  const [title, setTitle] = useState(video.title || "")
  const [description, setDescription] = useState(video.description || "")
  const [tags, setTags] = useState(video.tags?.join(", ") || "")

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      title,
      description,
      tags: tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-background rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-2xl font-bold text-primary mb-4">Edit Video</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Title</label>
            <Input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              rows={3}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Tags (comma-separated)</label>
            <Input type="text" value={tags} onChange={(e) => setTags(e.target.value)} />
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" loading={isLoading} className="flex-1">
              Save
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
